<template>
  <div class="end-kanban-board">
    <!-- Header Section -->
    <div class="header-section">
      <div class="page-title">
        <h2>末端揽收看板</h2>
        <span class="subtitle">Atualizado: {{ updateTime }} (Horário de Brasília)</span>
      </div>
      <div class="filter-section">
        <el-radio-group v-model="filter.period" size="small" style="margin-right: 15px">
          <el-radio-button label="yesterday">昨日</el-radio-button>
          <el-radio-button label="today">今日</el-radio-button>
          <el-radio-button label="month">本月</el-radio-button>
          <el-radio-button label="lastMonth">上月</el-radio-button>
        </el-radio-group>
        <el-date-picker
          v-model="filter.dateRange"
          type="daterange"
          size="small"
          range-separator="—"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          style="width: 240px; margin-right: 10px"
        />
        <el-select
          v-model="filter.stateList"
          placeholder="州"
          multiple
          collapse-tags
          clearable
          size="small"
          style="width: 150px; margin-right: 10px"
        >
          <el-option
            v-for="item in stateOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-select-v2
          v-model="filter.customerNameList"
          :options="customerList"
          placeholder="客户"
          multiple
          collapse-tags
          clearable
          filterable
          :loading="isLoadingCustomer"
          size="small"
          style="width: 200px; margin-right: 10px"
        />
        <el-button
          type="primary" icon="el-icon-search" size="small"
          @click="handleSearch"
        />
      </div>
    </div>

    <!-- 日环比 Comparativo diario -->
    <div class="metrics-container">
      <el-card shadow="hover" class="metric-card">
        <div slot="header" class="card-header">
          <span>日环比 Comparativo diario</span>
        </div>
        <el-row :gutter="20">
          <el-col v-for="(card, index) in metrics" :key="index" :span="6">
            <div class="metric-content">
              <div
                v-if="card.bgColor"
                class="metric-corner"
                :style="{ backgroundColor: card.bgColor }"
              />
              <div class="metric-info">
                <div class="metric-label">
                  {{ card.label }} <span class="metric-sublabel">({{ card.subLabel }})</span>
                </div>
                <div class="metric-value">{{ card.value }}</div>
                <div v-if="card.diff !== undefined" class="metric-trend">
                  <img :src="getTrendIcon(card.diff)" class="trend-icon" alt="trend">
                  <span
                    :class="getTrendTextClass(card.diff)"
                  >{{ Math.abs(card.diff).toFixed(2) }}%</span>
                </div>
                <div v-else class="metric-trend" />
              </div>
              <div class="metric-icon" :class="card.iconClass">
                <img
                  v-if="card.ringIcon" :src="card.ringIcon" class="ring-icon"
                  alt="ring"
                >
                <i v-else :class="card.icon" />
              </div>
            </div>
          </el-col>
        </el-row>
      </el-card>
    </div>

    <!-- Main Charts Row -->
    <el-row :gutter="20" style="margin-top: 20px">
      <!-- Left: Customer Collection Observation -->
      <el-col :span="14">
        <customer-collection-chart
          :customer-data="customerCollectionData"
          :daily-data="dailyCollectionData"
          @export="handleExportCustomerCollection"
        />
      </el-col>

      <!-- Right: Efficiency Monitors -->
      <el-col :span="10">
        <el-row :gutter="0">
          <el-col :span="24" style="margin-bottom: 20px">
            <customer-efficiency-chart
              :chart-data="customerEfficiencyData"
              @export="handleExportCustomerEfficiency"
            />
          </el-col>
          <el-col :span="24">
            <state-efficiency-chart
              :chart-data="stateEfficiencyData"
              @export="handleExportStateEfficiency"
            />
          </el-col>
        </el-row>
      </el-col>
    </el-row>

    <!-- Bottom Section -->
    <el-row :gutter="20" style="margin-top: 20px; margin-bottom: 20px">
      <!-- Left: Abnormality Table -->
      <el-col :span="14">
        <el-card shadow="hover" class="chart-card bottom-card" body-style="padding: 20px;">
          <div
            slot="header"
            style="
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
              font-size: 16px;
              color: #0f1728;
              font-style: normal;
              text-transform: none;
              font-weight: bold;
            "
          >
            <span>揽收异常件 Itens com anomalia na coleta</span>
            <img
              :src="exportIcon" class="export-btn" alt="导出"
              @click="handleExportAbnormal"
            >
          </div>
          <el-table
            v-loading="loading"
            :data="abnormalData"
            style="width: 100%"
            height="250"
            :header-cell-style="{ background: '#FFFFFF', color: '#666', fontWeight: 'normal' }"
          >
            <el-table-column
              prop="index" label="序号" width="80"
              align="center"
            />
            <el-table-column prop="reason" label="异常件原因(Motivo do item com anomalia)" />
            <el-table-column
              prop="problemNum"
              label="异常件数量(Qtd. de itens com anomalia)"
              align="center"
            />
            <el-table-column
              prop="problemRate"
              label="异常件占比(Taxa de itens com anomalia)"
              align="center"
              :formatter="(row) => row.problemRate + '%'"
            />
          </el-table>
        </el-card>
      </el-col>

      <!-- Right: Personnel Efficiency -->
      <el-col :span="10">
        <personnel-efficiency-stats
          :cache-time="cacheTime"
          :data="humanEfficiencyData"
          @export="handleExportPersonnelEfficiency"
        />
      </el-col>
    </el-row>
  </div>
</template>

<script>
import { apiCustomerPage } from '@/api/customer';
import { getDashboardChartData, getLgAreaListData } from '@/api/orderApi';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import ElSelectV2 from 'el-select-v2';
import { saveAs } from 'file-saver';
import CustomerCollectionChart from './components/CustomerCollectionChart';
import CustomerEfficiencyChart from './components/CustomerEfficiencyChart';
import PersonnelEfficiencyStats from './components/PersonnelEfficiencyStats';
import StateEfficiencyChart from './components/StateEfficiencyChart';

dayjs.extend(utc);
dayjs.extend(timezone);
const ExcelJS = require('exceljs');

export default {
  name: 'EndKanbanBoard',
  components: {
    CustomerCollectionChart, // 客户揽收情况图表组件
    CustomerEfficiencyChart, // 客户揽收效率图表组件
    StateEfficiencyChart, // 州揽收效率图表组件
    PersonnelEfficiencyStats, // 人效分析统计组件
    ElSelectV2 // 虚拟滚动选择器组件
  },
  data() {
    return {
      updateTime: dayjs().tz('America/Sao_Paulo').format('DD/MM/YYYY HH:mm:ss'), // 页面更新时间（巴西时间）
      cacheTime: dayjs().format('MM-DD HH:mm:ss'), // 缓存时间
      filter: {
        period: 'today', // 时间段快捷选择：yesterday(昨日)/today(今日)/month(本月)/lastMonth(上月)
        dateRange: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')], // 日期范围
        stateList: [], // 州列表（多选）
        customerNameList: ['总体', 'shein', 'sheind2d', 'szanjun', 'mandae', 'kwai'] // 客户名称列表（多选），使用 username
      },
      stateOptions: [], // 州选择器的选项列表
      customerList: [], // 客户选择器的选项列表
      isLoadingCustomer: false, // 客户列表加载状态
      isUpdatingStateList: false, // 标志位：防止州列表watch递归调用
      originalSidebarState: null, // 保存进入页面前的侧边栏状态
      metrics: [
        // 顶部指标卡片数据数组
        {
          label: '应收票数', // 主标签
          subLabel: 'Qtd. de voto', // 副标签（葡萄牙语）
          value: '96, 858', // 显示值
          diff: -1.64, // 环比差异百分比
          icon: 'el-icon-tickets', // 图标类名
          iconClass: 'icon-yellow' // 图标背景颜色类名
        },
        {
          label: '实收票数',
          subLabel: 'Qtd. de voto',
          value: '96, 858',
          diff: -1.64,
          icon: 'el-icon-box',
          iconClass: 'icon-blue'
        },
        {
          label: '揽收差异',
          subLabel: 'Diferença na coleta',
          value: '-858',
          diff: -1.64,
          icon: 'el-icon-warning-outline',
          iconClass: 'icon-red'
        },
        {
          label: '所需车辆',
          subLabel: 'Veículos',
          value: '58',
          diff: -1.64,
          icon: 'el-icon-truck',
          iconClass: 'icon-gray'
        }
      ],
      abnormalData: [], // 揽收异常件表格数据
      loading: false, // 全局加载状态
      // 图表数据
      customerCollectionData: [], // 客户揽收情况数据（按客户）
      dailyCollectionData: [], // 揽收单量每日趋势数据（按日期）
      customerEfficiencyData: [], // 客户揽收效率数据
      stateEfficiencyData: [], // 州揽收效率数据
      humanEfficiencyData: {
        staffCount: 0,
        collectCnt: 0,
        efficiencyRate: 0
      }, // 人效分析数据
      exportIcon: require('@/assets/images/kanban/endKanbanBoard/Export.svg'), // 导出图标
      ringYellowIcon: require('@/assets/images/kanban/endKanbanBoard/Ring-of-day-yellow.svg'), // 日环比黄色图标
      ringBlueIcon: require('@/assets/images/kanban/endKanbanBoard/Ring-of-day-blue.svg'), // 日环比蓝色图标
      ringRedIcon: require('@/assets/images/kanban/endKanbanBoard/Ring-of-day-red.svg') // 日环比红色图标
    };
  },
  watch: {
    // 监听时间段快捷选择的变化
    'filter.period'(val) {
      this.handlePeriodChange(val);
    },
    // 监听州列表的变化，实现全选/取消全选功能
    'filter.stateList'(newVal, oldVal) {
      this.handleStateListChange(newVal, oldVal);
    }
  },
  mounted() {
    // 保存进入页面前的侧边栏状态
    this.originalSidebarState = this.$store.state.app.sidebar.opened;

    // 关闭侧边栏（无动画）
    this.$store.dispatch('app/closeSideBar', { withoutAnimation: true });

    // 页面挂载时初始化数据
    this.initData();
  },
  beforeDestroy() {
    // 离开页面时，如果原本侧边栏是展开的，则恢复展开状态
    if (this.originalSidebarState) {
      this.$store.dispatch('app/toggleSideBar');
    }
  },
  methods: {
    /**
     * 初始化页面数据
     * 依次获取州列表、客户列表，然后加载所有图表数据
     */
    async initData() {
      await this.getStateList();
      this.getCustomerPage(); // 获取客户列表
      await this.fetchAllData();
    },

    /**
     * 获取客户列表
     * @param {string} name - 客户名称（用于搜索）
     * @param {array} customerList - 客户ID列表
     */
    async getCustomerPage() {
      try {
        this.isLoadingCustomer = true;
        const param = {
          pageNumber: 1,
          pageSize: 9999999, // 获取全量数据
          params: {
            quickSearch: ''
          }
        };

        const res = await apiCustomerPage(param, false);

        if (res.code === 200 && res.data && res.data.records) {
          // 格式化为 el-select-v2 需要的格式
          const list = res.data.records.map((item) => {
            return {
              value: item.username, // 使用 username 作为值
              label: `${item.username}` // 显示：客户别名(username)
            };
          });

          // 在列表最前面添加"总体"选项
          this.customerList = [
            {
              value: '总体',
              label: '总体'
            },
            ...list
          ];

          console.log('客户列表加载成功，共', this.customerList.length, '个客户');
        }
      } catch (err) {
        console.error('获取客户列表失败:', err);
        this.$message.error('获取客户列表失败');
        this.customerList = [];
      } finally {
        this.isLoadingCustomer = false;
      }
    },

    /**
     * 获取州列表（用于州选择器）
     * level参数说明：0=州，1=城市，2=地区
     */
    getStateList() {
      const params = {
        level: 0,
        parentId: ''
      };
      getLgAreaListData(params, false).then((res) => {
        if (res.code === 200) {
          this.stateOptions = res.data.map((item) => {
            return {
              value: item.nameEn,
              label: `${item.nameEn}`
            };
          });
          this.stateOptions.unshift({
            value: this.$t('GlobalSetObj.all'),
            label: this.$t('GlobalSetObj.all')
          });
        }
      });
    },

    /**
     * 获取揽收单量监控数据
     * 用于更新顶部4个指标卡片（应收票数、实收票数、揽收差异、所需车辆）
     */
    async getCollectionOrderRecord() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'collectionOrderRecord', // 单个字符串
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          // 更新顶部指标卡片数据
          this.updateMetrics(res.data.chartData);
        } else {
          // 数据为空时，重置为初始状态
          this.metrics = [
            {
              label: '应收票数',
              subLabel: 'Qtd. de voto',
              value: '0',
              icon: 'el-icon-tickets',
              iconClass: 'icon-yellow',
              bgColor: '#FEFBEA',
              ringIcon: this.ringYellowIcon
            },
            {
              label: '实收票数',
              subLabel: 'Qtd. de voto',
              value: '0',
              icon: 'el-icon-box',
              iconClass: 'icon-blue',
              bgColor: '#EFF6FF',
              ringIcon: this.ringBlueIcon
            },
            {
              label: '揽收差异',
              subLabel: 'Diferença na coleta',
              value: '0',
              icon: 'el-icon-warning-outline',
              iconClass: 'icon-red',
              bgColor: '#FEF2F2',
              ringIcon: this.ringRedIcon
            },
            {
              label: '所需车辆',
              subLabel: 'Veículos',
              value: '0',
              icon: 'el-icon-truck',
              iconClass: 'icon-gray',
              ringIcon: ''
            }
          ];
        }
      } catch (error) {
        console.error('获取揽收单量监控数据失败:', error);
        // 异常时也清空数据
        this.metrics = [
          {
            label: '应收票数',
            subLabel: 'Qtd. de voto',
            value: '0',
            icon: 'el-icon-tickets',
            iconClass: 'icon-yellow',
            bgColor: '#FEFBEA',
            ringIcon: this.ringYellowIcon
          },
          {
            label: '实收票数',
            subLabel: 'Qtd. de voto',
            value: '0',
            icon: 'el-icon-box',
            iconClass: 'icon-blue',
            bgColor: '#EFF6FF',
            ringIcon: this.ringBlueIcon
          },
          {
            label: '揽收差异',
            subLabel: 'Diferença na coleta',
            value: '0',
            icon: 'el-icon-warning-outline',
            iconClass: 'icon-red',
            bgColor: '#FEF2F2',
            ringIcon: this.ringRedIcon
          },
          {
            label: '所需车辆',
            subLabel: 'Veículos',
            value: '0',
            icon: 'el-icon-truck',
            iconClass: 'icon-gray',
            ringIcon: ''
          }
        ];
      }
    },

    /**
     * 获取揽收异常件数据
     * 用于底部左侧的异常件表格
     */
    async getCollectionExceptionRecord() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'collectionExceptionRecord',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          this.abnormalData = (res.data.chartData || []).map((item, index) => ({
            index: index + 1,
            reason: item.reason || '',
            problemRate: (Math.round(Number(item.problemRate) * 10000) / 100).toFixed(2),
            problemNum: item.problemNum
          }));
        } else {
          // 数据为空时，清空表格
          this.abnormalData = [];
        }
      } catch (error) {
        console.error('获取揽收异常件数据失败:', error);
        // 异常时也清空数据
        this.abnormalData = [];
      }
    },

    /**
     * 获取客户揽收情况观察数据
     * 用于左侧客户揽收情况图表
     */
    async getCustomerCollectionRecord() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'customerCollectionRecord',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          // 按客户分组的数据，用于上层柱状图
          this.customerCollectionData = res.data.chartData;
          console.log('客户揽收情况数据:', this.customerCollectionData);
        } else {
          // 数据为空时，清空图表
          this.customerCollectionData = [];
        }
      } catch (error) {
        console.error('获取客户揽收情况数据失败:', error);
        // 异常时也清空数据
        this.customerCollectionData = [];
      }
    },

    /**
     * 获取客户揽收效率监控数据
     * 用于右上角客户揽收效率图表
     */
    async getCustomerCollectionEfficiency() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'customerCollectionEfficiency',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          this.customerEfficiencyData = res.data.chartData;
          console.log('客户揽收效率数据:', this.customerEfficiencyData);
        } else {
          // 数据为空时，清空图表
          this.customerEfficiencyData = [];
        }
      } catch (error) {
        console.error('获取客户揽收效率数据失败:', error);
        // 异常时也清空数据
        this.customerEfficiencyData = [];
      }
    },

    /**
     * 获取揽收单量曲线图趋势数据
     * 用于揽收单量每日趋势曲线图
     */
    async getCollectionOrderRecordEveryDay() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'collectionOrderRecordEveryDay',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          // 按日期分组的数据，用于下层趋势图
          this.dailyCollectionData = res.data.chartData;
          console.log('揽收单量曲线图数据:', this.dailyCollectionData);
        } else {
          // 数据为空时，清空图表
          this.dailyCollectionData = [];
        }
      } catch (error) {
        console.error('获取揽收单量曲线图数据失败:', error);
        // 异常时也清空数据
        this.dailyCollectionData = [];
      }
    },

    /**
     * 获取州揽收效率监控数据
     * 用于右下角州揽收效率图表
     */
    async getStateCollectionEfficiency() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'stateCollectionEfficiency',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          this.stateEfficiencyData = res.data.chartData;
          console.log('州揽收效率数据:', this.stateEfficiencyData);
        } else {
          // 数据为空时，清空图表
          this.stateEfficiencyData = [];
        }
      } catch (error) {
        console.error('获取州揽收效率数据失败:', error);
        // 异常时也清空数据
        this.stateEfficiencyData = [];
      }
    },

    /**
     * 获取人效分析数据
     * 用于底部右侧人效分析统计
     */
    async getHumanEfficiencyAnalysis() {
      try {
        const params = this.buildQueryParams();
        const res = await getDashboardChartData({
          dashboardType: 'TERMINAL_COLLECTION',
          chartType: 'humanEfficiencyAnalysis',
          params
        });
        if (res.code === 200 && res.data && res.data.chartData) {
          // 人效分析数据包含: staffCount(上岗人数), collectCnt(揽收票数), efficiencyRate(平均人效)
          this.humanEfficiencyData = {
            staffCount: res.data.chartData.staffCount || 0,
            collectCnt: res.data.chartData.collectCnt || 0,
            efficiencyRate: res.data.chartData.efficiencyRate || 0
          };
          console.log('人效分析数据:', this.humanEfficiencyData);
        } else {
          // 如果没有数据，保持默认值
          this.humanEfficiencyData = {
            staffCount: 0,
            collectCnt: 0,
            efficiencyRate: 0
          };
        }
      } catch (error) {
        console.error('获取人效分析数据失败:', error);
        // 出错时也保持默认值
        this.humanEfficiencyData = {
          staffCount: 0,
          collectCnt: 0,
          efficiencyRate: 0
        };
      }
    },

    /**
     * 构建查询参数
     * 根据筛选条件构造API请求参数
     * @returns {object} 包含startDate、endDate、stateList、customerNameList的参数对象
     */
    buildQueryParams() {
      const params = {};

      // 日期范围
      if (this.filter.dateRange && this.filter.dateRange.length === 2) {
        params.startDate = dayjs(this.filter.dateRange[0]).format('YYYY-MM-DD');
        params.endDate = dayjs(this.filter.dateRange[1]).format('YYYY-MM-DD');
      }

      // 州列表
      if (this.filter.stateList && this.filter.stateList.length > 0) {
        params.stateList = this.filter.stateList;
      }

      // 客户名称列表
      if (this.filter.customerNameList && this.filter.customerNameList.length > 0) {
        params.customerNameList = this.filter.customerNameList;
      }

      return params;
    },

    /**
     * 更新顶部指标卡片数据
     * @param {object} data - 后端返回的指标数据
     */
    updateMetrics(data) {
      if (!data) return;

      this.metrics = [
        {
          label: '应收票数',
          subLabel: 'Qtd. de voto',
          value: this.formatNumber(data.exceptCollectCnt || 0),
          diff: Math.round(parseFloat(data.exceptCollectCntRate || 0) * 10000) / 100,
          icon: 'el-icon-tickets',
          iconClass: 'icon-yellow',
          bgColor: '#FEFBEA',
          ringIcon: this.ringYellowIcon
        },
        {
          label: '实收票数',
          subLabel: 'Qtd. de voto',
          value: this.formatNumber(data.actualCollectCnt || 0),
          diff: Math.round(parseFloat(data.actualCollectCntRate || 0) * 10000) / 100,
          icon: 'el-icon-box',
          iconClass: 'icon-blue',
          bgColor: '#EFF6FF',
          ringIcon: this.ringBlueIcon
        },
        {
          label: '揽收差异',
          subLabel: 'Diferença na coleta',
          value: this.formatNumber(data.collectDiffCnt || 0),
          diff: Math.round(parseFloat(data.collectDiffCntRate || 0) * 10000) / 100,
          icon: 'el-icon-warning-outline',
          iconClass: 'icon-red',
          bgColor: '#FEF2F2',
          ringIcon: this.ringRedIcon
        },
        {
          label: '所需车辆',
          subLabel: 'Veículos',
          value: data.needVel || '0',
          icon: 'el-icon-truck',
          iconClass: 'icon-gray'
        }
      ];
    },

    /**
     * 格式化数字（添加千分位分隔符）
     * @param {number} num - 要格式化的数字
     * @returns {string} 格式化后的字符串，如 "1,234,567"
     */
    formatNumber(num) {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    /**
     * 获取趋势图标
     * @param {number} diff - 差异值
     * @returns {string} 图标路径
     */
    getTrendIcon(diff) {
      if (diff > 0) {
        return require('@/assets/images/kanban/endKanbanBoard/Rise.svg');
      } else if (diff < 0) {
        return require('@/assets/images/kanban/endKanbanBoard/Fall.svg');
      } else {
        return require('@/assets/images/kanban/endKanbanBoard/Flat.svg');
      }
    },

    /**
     * 获取趋势文字颜色class
     * @param {number} diff - 差异值
     * @returns {string} 颜色class
     */
    getTrendTextClass(diff) {
      if (diff > 0) {
        return 'trend-up';
      } else if (diff < 0) {
        return 'trend-down';
      } else {
        return 'trend-flat';
      }
    },

    /**
     * 导出客户揽收情况观察数据
     */
    async handleExportCustomerCollection() {
      try {
        const wb = new ExcelJS.Workbook();

        // 按客户数据
        if (this.customerCollectionData.length > 0) {
          const sheet1 = wb.addWorksheet('按客户 Por cliente');
          sheet1.columns = [
            { header: '客户名称 Nome do cliente', key: 'customerName', width: 40 },
            {
              header: '应揽收票数 Qtd. de votos a serem coletados',
              key: 'exceptCollectCnt',
              width: 40
            },
            {
              header: '实际揽收票数 Qtd. de votos a real coletados',
              key: 'actualCollectCnt',
              width: 40
            },
            { header: '揽收差异 Diferença na qtd. de coletas', key: 'collectDiffCnt', width: 40 }
          ];
          this.customerCollectionData.forEach((item) => {
            sheet1.addRow({
              customerName: item.customerName,
              exceptCollectCnt: this.formatNumber(item.exceptCollectCnt),
              actualCollectCnt: this.formatNumber(item.actualCollectCnt),
              collectDiffCnt: this.formatNumber(item.collectDiffCnt)
            });
          });
        }

        // 按日期数据
        if (this.dailyCollectionData.length > 0) {
          const sheet2 = wb.addWorksheet('按日期 Por data');
          sheet2.columns = [
            { header: '日期 Data', key: 'dt', width: 25 },
            {
              header: '应揽收票数 Qtd. de votos a serem coletados',
              key: 'exceptCollectCnt',
              width: 40
            },
            {
              header: '实际揽收票数 Qtd. de votos a real coletados',
              key: 'actualCollectCnt',
              width: 40
            },
            { header: '揽收差异 Diferença na qtd. de coletas', key: 'collectDiffCnt', width: 40 }
          ];
          const sortedData = [...this.dailyCollectionData].sort(
            (a, b) => new Date(a.dt) - new Date(b.dt)
          );
          sortedData.forEach((item) => {
            sheet2.addRow({
              dt: item.dt,
              exceptCollectCnt: this.formatNumber(item.exceptCollectCnt),
              actualCollectCnt: this.formatNumber(item.actualCollectCnt),
              collectDiffCnt: this.formatNumber(item.collectDiffCnt)
            });
          });
        }

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(
          blob,
          `客户揽收情况观察 Observação da situação de coleta dos clientes_${dayjs().format(
            'YYYY-MM-DD_HHmmss'
          )}.xlsx`
        );
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败');
      }
    },

    /**
     * 导出揽收异常件数据
     */
    async handleExportAbnormal() {
      try {
        if (this.abnormalData.length === 0) {
          this.$message.warning('暂无数据可导出');
          return;
        }

        const wb = new ExcelJS.Workbook();
        const sheet = wb.addWorksheet('揽收异常件 Itens com anomalia na coleta');
        sheet.columns = [
          { header: '序号 Nº', key: 'index', width: 15 },
          { header: '异常件原因 Motivo do item com anomalia', key: 'reason', width: 50 },
          { header: '异常件数量 Qtd. de itens com anomalia', key: 'problemNum', width: 35 },
          { header: '异常件占比 Proporção de itens com anomalia', key: 'problemRate', width: 40 }
        ];

        this.abnormalData.forEach((item) => {
          sheet.addRow({
            index: item.index,
            reason: item.reason,
            problemNum: item.problemNum,
            problemRate: item.problemRate + '%'
          });
        });

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(
          blob,
          `揽收异常件 Itens com anomalia na coleta_${dayjs().format('YYYY-MM-DD_HHmmss')}.xlsx`
        );
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败');
      }
    },

    /**
     * 导出人效分析数据
     */
    async handleExportPersonnelEfficiency() {
      try {
        const wb = new ExcelJS.Workbook();
        const sheet = wb.addWorksheet('人效分析 Análise de eficiência');
        sheet.columns = [
          { header: '指标名称 Nome do indicador', key: 'name', width: 50 },
          { header: '数值 Valor', key: 'value', width: 20 }
        ];

        sheet.addRow({
          name: '上岗人数 Numero de pessoas no posto',
          value: this.humanEfficiencyData.staffCount || 0
        });
        sheet.addRow({
          name: '揽收票数 Numero de votos coletados',
          value: this.humanEfficiencyData.collectCnt || 0
        });
        sheet.addRow({
          name: '平均人效 Eficiência média por pessoa',
          value: this.humanEfficiencyData.efficiencyRate || 0
        });

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(blob, `人效分析 Análise de eficiência_${dayjs().format('YYYY-MM-DD_HHmmss')}.xlsx`);
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败');
      }
    },

    /**
     * 导出州揽收效率监控数据
     */
    async handleExportStateEfficiency() {
      try {
        if (this.stateEfficiencyData.length === 0) {
          this.$message.warning('暂无数据可导出');
          return;
        }

        const wb = new ExcelJS.Workbook();
        const sheet = wb.addWorksheet('州揽收效率监控');
        sheet.columns = [
          { header: '州 Estado', key: 'purposeState', width: 20 },
          { header: '揽收率(%) Taxa de coleta', key: 'collectionRate', width: 30 },
          { header: '揽收及时率(%) Taxa de coleta pontual', key: 'collectionOnTimeRate', width: 35 }
        ];

        this.stateEfficiencyData.forEach((item) => {
          sheet.addRow({
            purposeState: item.purposeState,
            collectionRate: (Math.round(Number(item.collectionRate) * 10000) / 100).toFixed(2),
            collectionOnTimeRate: (
              Math.round(Number(item.collectionOnTimeRate) * 10000) / 100
            ).toFixed(2)
          });
        });

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(
          blob,
          `州揽收效率监控 Monitoramento da eficiência de coleta de estado_${dayjs().format(
            'YYYY-MM-DD_HHmmss'
          )}.xlsx`
        );
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败');
      }
    },

    /**
     * 导出客户揽收效率监控数据
     */
    async handleExportCustomerEfficiency() {
      try {
        if (this.customerEfficiencyData.length === 0) {
          this.$message.warning('暂无数据可导出');
          return;
        }

        const wb = new ExcelJS.Workbook();
        const sheet = wb.addWorksheet('客户揽收效率监控');
        sheet.columns = [
          { header: '客户名称 Nome do cliente', key: 'customerName', width: 40 },
          { header: '揽收率(%) Taxa de coleta', key: 'collectionRate', width: 30 },
          { header: '揽收及时率(%) Taxa de coleta pontual', key: 'timelyRate', width: 35 }
        ];

        this.customerEfficiencyData.forEach((item) => {
          const collectionRate = item.collectionRate || item.collectRate || 0;
          const timelyRate =
            item.timelyRate || item.collectTimelyRate || item.collectionOnTimeRate || 0;
          sheet.addRow({
            customerName: item.customerName || item.name || '',
            collectionRate: (Math.round(Number(collectionRate) * 10000) / 100).toFixed(2),
            timelyRate: (Math.round(Number(timelyRate) * 10000) / 100).toFixed(2)
          });
        });

        const buffer = await wb.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/octet-stream' });
        saveAs(
          blob,
          `客户揽收效率监控 Monitoramento da eficiência de coleta do cliente_${dayjs().format(
            'YYYY-MM-DD_HHmmss'
          )}.xlsx`
        );
        this.$message.success('导出成功');
      } catch (error) {
        console.error('导出失败:', error);
        this.$message.error('导出失败');
      }
    },

    /**
     * 时间段快捷选择处理
     * @param {string} period - 时间段类型：yesterday/today/month/lastMonth
     */
    handlePeriodChange(period) {
      const today = dayjs();
      switch (period) {
        case 'yesterday':
          this.filter.dateRange = [
            today.subtract(1, 'day').format('YYYY-MM-DD'),
            today.subtract(1, 'day').format('YYYY-MM-DD')
          ];
          break;
        case 'today':
          this.filter.dateRange = [today.format('YYYY-MM-DD'), today.format('YYYY-MM-DD')];
          break;
        case 'month':
          this.filter.dateRange = [
            today.startOf('month').format('YYYY-MM-DD'),
            today.format('YYYY-MM-DD')
          ];
          break;
        case 'lastMonth':
          this.filter.dateRange = [
            today.subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
            today.subtract(1, 'month').endOf('month').format('YYYY-MM-DD')
          ];
          break;
      }
      this.handleSearch();
    },

    /**
     * 查询所有图表数据
     * 并行请求所有7个图表的数据接口，提高加载效率
     */
    async fetchAllData() {
      this.loading = true;
      try {
        await Promise.all([
          this.getCollectionOrderRecord(), // 揽收单量监控
          this.getCustomerCollectionRecord(), // 客户揽收情况观察
          this.getCustomerCollectionEfficiency(), // 客户揽收效率监控
          this.getCollectionOrderRecordEveryDay(), // 揽收单量曲线图趋势
          this.getStateCollectionEfficiency(), // 州揽收效率监控
          this.getCollectionExceptionRecord(), // 揽收异常件
          this.getHumanEfficiencyAnalysis() // 人效分析
        ]);
        this.updateTime = dayjs().tz('America/Sao_Paulo').format('DD/MM/YYYY HH:mm:ss');
      } catch (error) {
        this.$message.error('数据加载失败');
      } finally {
        this.loading = false;
      }
    },

    /**
     * 查询按钮点击处理
     * 根据当前筛选条件重新加载所有数据
     */
    handleSearch() {
      this.fetchAllData();
    },

    /**
     * 处理州列表变化，实现全选/取消全选功能
     * @param {Array} newVal - 新的州列表
     * @param {Array} oldVal - 旧的州列表
     */
    handleStateListChange(newVal, oldVal) {
      // 避免递归调用
      if (this.isUpdatingStateList) {
        return;
      }

      const allValue = this.$t('GlobalSetObj.all');

      // 如果新值包含"全部"，但旧值不包含"全部" -> 用户刚选中了"全部"
      if (newVal.includes(allValue) && !oldVal.includes(allValue)) {
        this.isUpdatingStateList = true;
        // 选中所有州（包括"全部"）
        this.filter.stateList = this.stateOptions.map((item) => item.value);
        this.$nextTick(() => {
          this.isUpdatingStateList = false;
        });
      } else if (!newVal.includes(allValue) && oldVal.includes(allValue)) {
        // 如果旧值包含"全部"，但新值不包含"全部" -> 用户取消了"全部"
        this.isUpdatingStateList = true;
        // 清空所有选中
        this.filter.stateList = [];
        this.$nextTick(() => {
          this.isUpdatingStateList = false;
        });
      } else if (
        newVal.includes(allValue) &&
        oldVal.includes(allValue) &&
        newVal.length < this.stateOptions.length
      ) {
        // 如果新值包含"全部"，但有其他州被取消了 -> 自动取消"全部"
        this.isUpdatingStateList = true;
        // 从列表中移除"全部"
        this.filter.stateList = newVal.filter((item) => item !== allValue);
        this.$nextTick(() => {
          this.isUpdatingStateList = false;
        });
      } else if (!newVal.includes(allValue) && newVal.length === this.stateOptions.length - 1) {
        // 如果所有州都被选中了（除了"全部"），自动选中"全部"
        this.isUpdatingStateList = true;
        // 添加"全部"到列表
        this.filter.stateList = [...newVal, allValue];
        this.$nextTick(() => {
          this.isUpdatingStateList = false;
        });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.end-kanban-board {
  padding: 20px;
  background-color: #f0f2f5;
  min-height: 100vh;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  background: #fff;
  padding: 15px 20px;
  border-radius: 4px;

  .page-title {
    h2 {
      margin: 0;
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-weight: normal;
      font-size: 30px;
      color: #0f172a;
      font-style: normal;
      text-transform: none;
      font-style: normal;
      text-transform: none;
    }
    .subtitle {
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-weight: normal;
      font-size: 16px;
      color: #a8aeb9;
      margin-top: 5px;
      display: block;
    }
  }
}

.metric-card {
  margin-bottom: 0;
  border: none;
  background: #ffffff;
  border-radius: 12px;

  ::v-deep .el-card__header {
    border-bottom: none;
    padding-bottom: 0;
  }

  ::v-deep .el-card__body {
    padding: 20px;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
    font-weight: bold;
    font-size: 16px;
    color: #0f1728;
  }

  .metric-content {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    background: #f9f9f9;
    padding: 20px 0px 16px 10px;
    margin-bottom: 24px;
    border-radius: 12px;
    overflow: hidden;
  }

  .metric-corner {
    position: absolute;
    top: 0;
    right: 0;
    width: 92px;
    height: 52px;
    border-radius: 0 12px 0 100%;
  }

  .metric-info {
    .metric-label {
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-weight: normal;
      font-size: 14px;
      color: #6a7382;
      font-style: normal;
      text-transform: none;
      margin-bottom: 10px;
      position: relative;
      z-index: 1;
    }
    .metric-sublabel {
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-weight: normal;
      font-size: 14px;
      color: #a8aeb9;
      font-style: normal;
      text-transform: none;
    }
    .metric-value {
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-weight: normal;
      font-size: 24px;
      color: #0f1728;
      font-style: normal;
      text-transform: none;
      margin-bottom: 10px;
    }
    .metric-trend {
      min-height: 20px; /* 即使没有内容也要占位 */
      font-size: 12px;
      display: flex;
      align-items: center;

      .trend-icon {
        width: 20px;
        height: auto;
      }

      span {
        font-weight: 500;
        font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
        font-weight: normal;
        font-size: 13px;
        color: #2aab80;
        font-style: normal;
        text-transform: none;
        margin-left: 16px;
        &.trend-up {
          color: #2aab80;
        }

        &.trend-down {
          color: #b74545;
        }

        &.trend-flat {
          color: #f97216;
        }
      }
    }
  }

  .metric-icon {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    z-index: 99;
    margin-right: 23px;

    .ring-icon {
      width: 21px;
      height: 21px;
    }

    &.icon-yellow {
      background: #fdf2c5;
      color: #eeaf0c;
    }
    &.icon-blue {
      background: #dbeafe;
      color: #3e5dac;
    }
    &.icon-red {
      background: #fee1e1;
      color: #b74545;
    }
    &.icon-gray {
      background: #f5f5f5;
      color: #666;
    }
  }
}

.chart-card {
  margin-bottom: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 12px;
  border: none;

  ::v-deep .el-card__header {
    border-bottom: none;
    padding-bottom: 0;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
      font-family: Alibaba PuHuiTi 2, Alibaba PuHuiTi 20;
      font-size: 24px;
      color: #0f1728;
      font-style: normal;
      text-transform: none;
      font-weight: bold;
    }
    .export-btn {
      width: 16px;
      height: 16px;
      cursor: pointer;
      transition: opacity 0.3s;

      &:hover {
        opacity: 0.7;
        cursor: pointer;
      }
    }
  }

  &.bottom-card {
    height: auto;
  }
}
</style>
